import {
  ApiMasterLabelCrop,
  ApiMasterLabelUPCData,
  ForgeDesignGroupSummary,
  MasterCropData,
  MasterDesignData,
  MasterUPCData,
} from '../types';

function optionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function extractBarcodeFromDesignImageUrls(...urls: Array<unknown>): string | undefined {
  for (const url of urls) {
    const text = optionalString(url);
    if (!text) continue;
    const matches = text.match(/\d{8,14}/g);
    if (!matches || matches.length === 0) continue;
    return matches[matches.length - 1];
  }
  return undefined;
}

function transformCrop(api: ApiMasterLabelCrop): MasterCropData {
  return {
    id: api.cropId,
    imageUrl: api.vizUrl,
    isInactive: !api.isActive,
    isPending: api.isPending,
    isMismatch: api.pendingReason === 'unknown_upc',
    storeId: String(api.storeId),
    itemNumber: api.itemNumber || undefined,
    source: api.source || undefined,
    forgeOriginSource: api.forgeOriginSource || undefined,
    barcode: optionalString(api.barcode),
    normalizedBarcode: optionalString(api.normalizedBarcode),
    normalizedUpc: optionalString(api.normalizedUpc),
    createdAt: api.createdAt || undefined,
    orientation: api.orientation || undefined,
    classificationType: api.classificationType || undefined,
    reviewIssueType: api.reviewIssueType ?? undefined,
    imageResolution: api.imageResolution || undefined,
    createdBy: api.createdBy || undefined,
    updatedAt: api.updatedAt || undefined,
    updatedBy: api.updatedBy || undefined,
  };
}

function transformGroupSummary(api: ForgeDesignGroupSummary): ForgeDesignGroupSummary {
  return {
    designId: api.designId,
    normalizedBarcode: optionalString(api.normalizedBarcode) ?? null,
    previewVizUrl: optionalString(api.previewVizUrl) ?? null,
    forgeUrl: optionalString(api.forgeUrl) ?? null,
  };
}

export function transformApiUPCData(api: ApiMasterLabelUPCData): MasterUPCData {
  const itemNumber = optionalString(api.itemNumber);
  if (!itemNumber) {
    throw new Error(`Master Label payload missing itemNumber for UPC ${api.upc}`);
  }
  const designs = api.designs
    .map((design): MasterDesignData => {
      const crops = (design.crops ?? []).map(transformCrop);
      const persistedDesignId = optionalString(design._id) || '';
      const representativeCropId =
        optionalString(design.representativeCropId) || crops[0]?.id || null;
      const frontImageUrl = optionalString(design.frontImageUrl);
      const backImageUrl = optionalString(design.backImageUrl);
      const barcodeImageUrl = optionalString(design.barcodeImageUrl);
      const normalizedBarcode =
        optionalString(design.normalizedBarcode) ||
        optionalString(design.barcode) ||
        crops[0]?.normalizedBarcode ||
        extractBarcodeFromDesignImageUrls(barcodeImageUrl, frontImageUrl, backImageUrl);

      return {
        id: persistedDesignId,
        persistedDesignId,
        representativeCropId,
        barcodeStatus: design.barcodeStatus,
        frontImageUrl,
        backImageUrl,
        barcodeImageUrl,
        note: optionalString(design.note) ?? null,
        normalizedBarcode,
        fullGroupDesigns: (design.fullGroupDesigns ?? []).map(transformGroupSummary),
        nonFrontGroupDesigns: (design.nonFrontGroupDesigns ?? []).map(transformGroupSummary),
        crops,
      };
    })
    .filter((design) => design.crops.length > 0 || Boolean(design.normalizedBarcode));

  return {
    upc: api.upc,
    normalizedUpc: api.normalizedUpc,
    productName: api.productName || '',
    productImageUrl: api.productImageUrl || undefined,
    lookupStoreId: api.lookupStoreId ?? null,
    alternativeImageUrls:
      api.alternativeImageUrls && api.alternativeImageUrls.length > 0
        ? api.alternativeImageUrls
        : api.productImageUrl
          ? [api.productImageUrl]
          : undefined,
    itemNumber,
    barcodeSummary: api.barcodeSummary
      ? {
          primaryUpcs: api.barcodeSummary.primaryUpcs.map((entry) => ({
            upc: entry.upc,
            provenance: entry.provenance,
            storeDetails: entry.storeDetails.map((detail) => ({
              storeLabel: detail.storeLabel,
              role: detail.role,
              name: detail.name || undefined,
              casePack: detail.casePack || undefined,
              images: detail.images.map((image) => ({
                url: image.url,
                sourceLabel: image.sourceLabel || undefined,
              })),
            })),
          })),
          alternateUpcs: api.barcodeSummary.alternateUpcs.map((entry) => ({
            upc: entry.upc,
            provenance: entry.provenance,
            storeDetails: entry.storeDetails.map((detail) => ({
              storeLabel: detail.storeLabel,
              role: detail.role,
              name: detail.name || undefined,
              casePack: detail.casePack || undefined,
              images: detail.images.map((image) => ({
                url: image.url,
                sourceLabel: image.sourceLabel || undefined,
              })),
            })),
          })),
          casePacks: api.barcodeSummary.casePacks.map((entry) => ({
            upc: entry.upc,
            provenance: entry.provenance,
            storeDetails: entry.storeDetails.map((detail) => ({
              storeLabel: detail.storeLabel,
              role: detail.role,
              name: detail.name || undefined,
              casePack: detail.casePack || undefined,
              images: detail.images.map((image) => ({
                url: image.url,
                sourceLabel: image.sourceLabel || undefined,
              })),
            })),
          })),
        }
      : undefined,
    warnings: (api.warnings ?? []).map((warning) => ({
      code: warning.code,
      message: warning.message,
      designId: optionalString(warning.designId),
      normalizedBarcode: optionalString(warning.normalizedBarcode),
      resolvedVia: optionalString(warning.resolvedVia),
    })),
    snapshotVersion: api.snapshotVersion,
    designs,
    pendingCrops: api.pendingCrops.map(transformCrop),
    unassignedCrops: api.unassignedCrops.map(transformCrop),
    cartonCrops: api.cartonCrops.map(transformCrop),
    inactiveCrops: api.inactiveCrops.map(transformCrop),
  };
}
